import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useParams, useRoutes } from 'react-router-dom';
import GuestGuard from 'src/auth/GuestGuard';
import { LoadingScreen, SplashScreen } from 'src/components/loading-screen';
import { CONFIG } from 'src/config-global';
import { AuthSplitLayout } from 'src/layouts/auth-split';
import { DashboardLayout } from 'src/layouts/dashboard';
import { paths } from 'src/routes/paths';
import AuthGuard from '../auth/AuthGuard';

// ----------------------------------------------------------------------

function RedirectToInvoiceDetail() {
  const { id } = useParams();
  return <Navigate to={paths.dashboard.invoices.detail(id ?? '')} replace />;
}

// ----------------------------------------------------------------------

export function Router() {
  const layoutContent = (
    <DashboardLayout>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    </DashboardLayout>
  );

  return useRoutes([
    {
      path: '/',
      element: <Navigate to={CONFIG.auth.redirectPath} replace />,
    },
    {
      path: 'dashboard',
      element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,

      children: [
        { element: <Navigate to="overview" replace />, index: true },
        { path: 'overview', element: <PageOverview /> },
        { path: 'your-wallets', element: <Navigate to="/dashboard/overview" replace /> },
        { path: 'wallets', element: <PageWallets /> },
        { path: 'transfer', element: <PageTransfer /> },
        { path: 'transactions', element: <PageTransactions /> },
        { path: 'gas', element: <PageGas /> },
        { path: 'customers', element: <PageCustomers /> },
        { path: 'customers/:id', element: <PageCustomer /> },
        { path: 'webhooks', element: <PageWebhooks /> },
        { path: 'treasury-auto-conversion', element: <PageTreasuryAutoConversion /> },
        { path: 'keys', element: <PageAPIKeys /> },
        { path: 'terminals', element: <PageTerminals /> },
        {
          path: 'checkout',
          element: <CheckoutLayout />,
          children: [
            { element: <Navigate to="analytics" replace />, index: true },
            { path: 'analytics', element: <PageCheckoutAnalytics /> },
            { path: 'wallets', element: <PageCheckoutWallets /> },
            { path: 'transactions', element: <PageCheckoutTransactions /> },
            { path: 'products', element: <PageProducts /> },
            { path: 'sessions', element: <PageCheckoutSessions /> },
            { path: 'auto-conversion', element: <PageCheckoutAutoConversion /> },
            { path: 'invoices', element: <PageInvoicesList /> },
            { path: 'invoices/create', element: <PageCreateInvoice /> },
            { path: 'invoices/:id', element: <PageInvoiceDetail /> },
          ],
        },
        { path: 'products', element: <Navigate to="/dashboard/checkout/products" replace /> },
        {
          path: 'near-intents',
          element: <NearIntentsLayout />,
          children: [
            { path: '', element: <PageNearIntentsList /> },
            { path: ':id', element: <PageNearIntentDetail /> },
          ],
        },
        {
          path: 'customer-swaps',
          element: <CustomerSwapsLayout />,
          children: [
            { path: '', element: <PageCustomerSwapsList /> },
            { path: ':id', element: <PageCustomerSwapDetail /> },
          ],
        },
        {
          path: 'swap',
          element: <SwapLayout />,
          children: [
            { path: '', element: <PageSwapList /> },
            { path: ':id', element: <PageSwapDetail /> },
          ],
        },
        {
          path: 'batch-payouts',
          element: <BatchPayoutsLayout />,
          children: [
            { path: '', element: <PageBatchPayoutList /> },
            { path: 'funding', element: <PageBatchPayoutFunding /> },
            { path: 'build', element: <PageBatchPayoutBuild /> },
            { path: 'transactions', element: <PageBatchPayoutTransactions /> },
            { path: ':id', element: <PageBatchPayoutDetail /> },
          ],
        },
        // Invoices moved under Checkout; notification emails still link to the old paths.
        { path: 'invoices', element: <Navigate to={paths.dashboard.invoices.root} replace /> },
        {
          path: 'invoices/create',
          element: <Navigate to={paths.dashboard.invoices.create} replace />,
        },
        { path: 'invoices/:id', element: <RedirectToInvoiceDetail /> },
        {
          path: 'billing',
          element: <BillingLayout />,
          children: [
            { path: '', element: <PageBillingOverview /> },
            { path: 'total', element: <PageBillingTotal /> },
            { path: 'history', element: <PageBillingHistory /> },
          ],
        },
        {
          path: 'reports',
          element: <ReportsLayout />,
          children: [
            { element: <Navigate to="default" replace />, index: true },
            { path: 'default', element: <PageReportsDefault /> },
            { path: 'pay', element: <PageReportsPay /> },
            { path: 'payouts', element: <PageReportsPayouts /> },
            { path: 'swaps', element: <PageReportsSwaps /> },
          ],
        },
        { path: 'user', element: <PageUserSettings /> },
        { path: 'organization', element: <PageOrganizationSettings /> },
        {
          path: 'transactions-to-approve',
          element: <Navigate to={paths.dashboard.transactionsToApprove} replace />,
        },
      ],
    },
    { path: '404', element: <Page404 /> },
    {
      element: (
        <GuestGuard>
          <Suspense fallback={<SplashScreen />}>
            <Outlet />
          </Suspense>
        </GuestGuard>
      ),
      children: [
        {
          path: 'sign-in',
          element: (
            <AuthSplitLayout section={{ title: 'Hi, Welcome back' }}>
              <SignInPage />
            </AuthSplitLayout>
          ),
        },
        {
          path: 'sign-up',
          element: (
            <AuthSplitLayout>
              <SignUpPage />
            </AuthSplitLayout>
          ),
        },
        {
          path: 'forgot-password',
          element: (
            <AuthSplitLayout>
              <ForgotPasswordPage />
            </AuthSplitLayout>
          ),
        },

        {
          path: 'reset-password',
          element: (
            <AuthSplitLayout>
              <ResetPasswordPage />
            </AuthSplitLayout>
          ),
        },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

const SignInPage = lazy(() => import('src/pages/auth/sign-in'));
const SignUpPage = lazy(() => import('src/pages/auth/sign-up'));

const ResetPasswordPage = lazy(() => import('src/pages/auth/reset-password'));
const ForgotPasswordPage = lazy(() => import('src/pages/auth/forgot-password'));
const PageOverview = lazy(() => import('src/pages/dashboard/page-overview'));
const PageWallets = lazy(() => import('src/pages/dashboard/page-wallets'));
const PageTransfer = lazy(() => import('src/pages/dashboard/page-transfer'));
const PageTransactions = lazy(() => import('../pages/dashboard/transactions'));
const PageGas = lazy(() => import('../pages/dashboard/gas'));
const PageCustomers = lazy(() => import('../pages/dashboard/customers'));
const PageCustomer = lazy(() => import('../pages/dashboard/customer'));
const PageAPIKeys = lazy(() => import('../pages/dashboard/keys'));
const PageTerminals = lazy(() => import('../pages/dashboard/terminals'));
const PageUserSettings = lazy(() => import('../pages/dashboard/user'));
const PageOrganizationSettings = lazy(() => import('../pages/dashboard/organization'));
const PageWebhooks = lazy(() => import('../pages/dashboard/webhooks'));
const PageTreasuryAutoConversion = lazy(
  () => import('../pages/dashboard/treasury-auto-conversion'),
);
const PageProducts = lazy(() => import('../pages/dashboard/products'));
const SwapLayout = lazy(() => import('../pages/dashboard/swap/layout'));
const NearIntentsLayout = lazy(() => import('../pages/dashboard/near-intents/layout'));
const PageNearIntentsList = lazy(() => import('../pages/dashboard/near-intents'));
const PageNearIntentDetail = lazy(() => import('../pages/dashboard/near-intents/detail'));
const CustomerSwapsLayout = lazy(() => import('../pages/dashboard/customer-swaps/layout'));
const PageCustomerSwapsList = lazy(() => import('../pages/dashboard/customer-swaps'));
const PageCustomerSwapDetail = lazy(() => import('../pages/dashboard/customer-swaps/detail'));
const PageSwapList = lazy(() => import('../pages/dashboard/swap/index'));
const PageSwapDetail = lazy(() => import('../pages/dashboard/swap/detail'));
const BatchPayoutsLayout = lazy(
  () => import('../pages/dashboard/batch-payouts/layout'),
);
const PageBatchPayoutFunding = lazy(
  () => import('../pages/dashboard/batch-payouts/funding'),
);
const PageBatchPayoutList = lazy(
  () => import('../pages/dashboard/batch-payouts/list'),
);
const PageBatchPayoutBuild = lazy(
  () => import('../pages/dashboard/batch-payouts/build'),
);
const PageBatchPayoutDetail = lazy(
  () => import('../pages/dashboard/batch-payouts/detail'),
);
const PageBatchPayoutTransactions = lazy(
  () => import('../pages/dashboard/batch-payouts/transactions'),
);
const CheckoutLayout = lazy(() => import('../pages/dashboard/checkout/layout'));
const PageCheckoutAnalytics = lazy(() => import('../pages/dashboard/checkout/analytics'));
const PageCheckoutWallets = lazy(() => import('../pages/dashboard/checkout/wallets'));
const PageCheckoutTransactions = lazy(() => import('../pages/dashboard/checkout/transactions'));
const PageCheckoutSessions = lazy(() => import('../pages/dashboard/checkout/sessions'));
const PageCheckoutAutoConversion = lazy(
  () => import('../pages/dashboard/checkout/auto-conversion'),
);
const PageInvoicesList = lazy(() => import('../pages/dashboard/invoices/index'));
const PageCreateInvoice = lazy(() => import('../pages/dashboard/invoices/create'));
const PageInvoiceDetail = lazy(() => import('../pages/dashboard/invoices/detail'));
const BillingLayout = lazy(() => import('../pages/dashboard/billing/layout'));
const PageBillingOverview = lazy(() => import('../pages/dashboard/billing/index'));
const PageBillingTotal = lazy(() => import('../pages/dashboard/billing/total'));
const PageBillingHistory = lazy(() => import('../pages/dashboard/billing/history'));
const ReportsLayout = lazy(() => import('../pages/dashboard/reports/layout'));
const PageReportsDefault = lazy(() => import('../pages/dashboard/reports/default'));
const PageReportsPay = lazy(() => import('../pages/dashboard/reports/pay'));
const PageReportsPayouts = lazy(() => import('../pages/dashboard/reports/payouts'));
const PageReportsSwaps = lazy(() => import('../pages/dashboard/reports/swaps'));
const Page404 = lazy(() => import('src/pages/error/404'));
